/**
 * NAMA booth showcase: content and URLs.
 * Routes: `/showcase` (Configure), `/showcase/environments`.
 */

export type VisualMode = 'configurator' | 'environment'

export const DEFAULT_VISUAL_MODE: VisualMode = 'configurator'

/** Appended to the SwiftXR page URL for phone QR (matches iframe configuration). */
export const SWIFTXR_QR_LAUNCH_QUERY = 'launch=iwn9'

const SWIFTXR_QR_API = 'https://api.swiftxr.io/v1/sites/qrcode'

/** Smart Store right-column headline (shell has logo + tabs only). */
export const SHOWCASE_BOOTH_HEADLINE = 'Win the best locations.'

export const SHOWCASE_SMART_STORE_TAGLINE = SHOWCASE_BOOTH_HEADLINE

/** Supporting line under the Smart Store headline. */
export const SHOWCASE_SMART_STORE_SUBLINE =
	'Fit anywhere with a Smart Store designed for your space.'

/** Smart Store instruction — two lines in the info column. */
export const SHOWCASE_CONFIGURATOR_BODY_LINE1 = 'Pick a configuration.'

export const SHOWCASE_CONFIGURATOR_BODY_LINE2 =
	'Take a card to place it in any space with AR.'

/** Full text for non-React consumers / docs. */
export const SHOWCASE_CONFIGURATOR_BODY =
	`${SHOWCASE_CONFIGURATOR_BODY_LINE1} ${SHOWCASE_CONFIGURATOR_BODY_LINE2}`

/** “Where it fits” tab carousel intro (route `/showcase/environments`). */
export const SHOWCASE_ENVIRONMENTS_INTRO = 'Swipe to see where it fits.'

/** One line after scan (SwiftXR AR placement on device). */
export const SHOWCASE_AR_HINT =
	'Use AR to preview and position it in real life.'

/** Same copy as `SHOWCASE_AR_HINT` (legacy export name used by showcase UI). */
export const SHOWCASE_AR_OUTCOME_TITLE = SHOWCASE_AR_HINT

/** Hero / trailer video (`/video`). Cloudinary-hosted; requires network. */
export const SHOWCASE_DEMO_VIDEO_URL =
	'https://res.cloudinary.com/kitchenmate/video/upload/v1773689158/Website/Micromart/Website_hero_video.mp4'

export type CabinetConfigurationId =
	| 'single'
	| 'double'
	| 'triple'
	| 'doubleEnclosure'
	| 'tripleEnclosure'

export interface CabinetConfiguration {
	id: CabinetConfigurationId
	label: string
	/** SwiftXR scene URL (iframe src, no launch query). */
	swiftXrUrl: string
	/** Public path under `public/` or bundled asset URL */
	thumbnailSrc: string
	shortDescription: string
}

/**
 * Cabinet 3D scenes + thumbnails.
 * Update `swiftXrUrl` here when SwiftXR links change.
 * Update `thumbnailSrc` when replacing files in `public/showcase/thumbnails/`.
 */
export const cabinetConfigurations: CabinetConfiguration[] = [
	{
		id: 'single',
		label: '1 Cabinet',
		swiftXrUrl: 'https://3d.micromart.com/micromart-single',
		thumbnailSrc: `${process.env.PUBLIC_URL}/showcase/thumbnails/single.png`,
		shortDescription: 'Compact single-cabinet Smart Store layout.',
	},
	{
		id: 'double',
		label: '2 Cabinets',
		swiftXrUrl: 'https://3d.micromart.com/micromart-double',
		thumbnailSrc: `${process.env.PUBLIC_URL}/showcase/thumbnails/double.png`,
		shortDescription: 'Side-by-side dual cabinet configuration.',
	},
	{
		id: 'triple',
		label: '3 Cabinets',
		swiftXrUrl: 'https://3d.micromart.com/micromart-tripple',
		thumbnailSrc: `${process.env.PUBLIC_URL}/showcase/thumbnails/triple.png`,
		shortDescription: 'Three-cabinet lineup for higher capacity.',
	},
	{
		id: 'doubleEnclosure',
		label: '2 + Enclosure',
		swiftXrUrl: 'https://3d.micromart.com/micromart-double-enclosure',
		thumbnailSrc: `${process.env.PUBLIC_URL}/showcase/thumbnails/double-enclosure.png`,
		shortDescription: 'Two cabinets with integrated enclosure.',
	},
	{
		id: 'tripleEnclosure',
		label: '3 + Enclosure',
		swiftXrUrl: 'https://3d.micromart.com/micromart-tripple-enclosure',
		thumbnailSrc: `${process.env.PUBLIC_URL}/showcase/thumbnails/triple-enclosure.png`,
		shortDescription: 'Three cabinets with enclosure option.',
	},
]

export const DEFAULT_CABINET_ID: CabinetConfigurationId = 'single'

/** Optional override if the QR should open a landing page instead of the active scene */
export const SHOWCASE_QR_URL_OVERRIDE: string | null = null

export function getSwiftXrQrImageUrl(sceneUrl: string): string {
	const target =
		SHOWCASE_QR_URL_OVERRIDE ??
		`${sceneUrl}${sceneUrl.includes('?') ? '&' : '?'}${SWIFTXR_QR_LAUNCH_QUERY}`
	const data = encodeURIComponent(target)
	return `${SWIFTXR_QR_API}?size=512x512&data=${data}`
}

/** Environment imagery (context scenes) for `/showcase/environments`. */
export interface EnvironmentPhoto {
	id: string
	label: string
	imageUrl: string
	shortDescription: string
}

export const environmentPhotos: EnvironmentPhoto[] = [
	{
		id: 'hospital',
		label: 'Hospital',
		imageUrl:
			'https://cdn.prod.website-files.com/66d10f2f9108642818b7a365/69ad96b2baff849bf5fd2cbf_a1492a8984912cb775e6efdc4c5bdc8e_Hospital.jpeg',
		shortDescription: 'Care settings and staff touchpoints.',
	},
	{
		id: 'school',
		label: 'School',
		imageUrl:
			'https://cdn.prod.website-files.com/66d10f2f9108642818b7a365/69b848a37dfb54262609c468_3fb8f1219ec6d4de15376227daaa5e78_School.jpeg',
		shortDescription: 'Campuses and student high-traffic areas.',
	},
	{
		id: 'apartment',
		label: 'Apartment',
		imageUrl:
			'https://cdn.prod.website-files.com/66d10f2f9108642818b7a365/69ad96b24a80dcd5aa3de3c7_1b15f3c857cecf4feeadca21d395dda6_Apartment.jpeg',
		shortDescription: 'Residential amenities and shared spaces.',
	},
	{
		id: 'government',
		label: 'Government',
		imageUrl:
			'https://cdn.prod.website-files.com/66d10f2f9108642818b7a365/69ad96b2c1bed6eee76f0e75_03fcb38c348474b3a1933a4d7690fac1_Government.jpeg',
		shortDescription: 'Public buildings and secure facilities.',
	},
	{
		id: 'office',
		label: 'Office',
		imageUrl:
			'https://cdn.prod.website-files.com/66d10f2f9108642818b7a365/69ad96b24961698d50071d84_40b5f4208abc1eeeaef4c5fa289d365c_Office.jpeg',
		shortDescription: 'Workplaces and collaboration hubs.',
	},
	{
		id: 'gym',
		label: 'Gym',
		imageUrl:
			'https://cdn.prod.website-files.com/66d10f2f9108642818b7a365/69ad96b2422ed7d6693d8e24_07b294642965ac41f0139793fb958fc7_Gym.jpeg',
		shortDescription: 'Fitness centers and athletic venues.',
	},
]

export const DEFAULT_ENVIRONMENT_ID = environmentPhotos[0].id

